<template>
  <tr
    :data-pivot-id="resource.id.pivotValue"
    :dusk="`${resource.id.value}-row`"
    class="group border-b border-zinc-200/60 transition-colors last:border-0 hover:bg-zinc-50/80 dark:border-zinc-800 dark:hover:bg-zinc-800/40"
    :class="{
      'divide-x divide-zinc-200/70 dark:divide-zinc-800': shouldShowColumnBorders,
      'panel-table-row--selected': checked,
    }"
    @click.stop.prevent="handleClick"
  >
    <!-- Resource Selection Checkbox -->
    <td
      v-if="shouldShowCheckboxes"
      :class="{
        'py-2': !shouldShowTight,
        'cursor-pointer': resource.authorizedToView,
      }"
      class="td-fit ps-5 pe-5 py-4"
      @click.stop
    >
      <Checkbox
        v-if="shouldShowCheckboxes"
        @change="toggleSelection"
        :model-value="checked"
        :dusk="`${resource.id.value}-checkbox`"
        :aria-label="__('Select Resource :title', { title: resource.title })"
      />
    </td>

    <!-- Fields -->
    <td
      v-for="(field, index) in resource.fields"
      :key="field.uniqueKey"
      :class="{
        'ps-5 pe-5 py-4': true,
        'cursor-pointer': clickableRow,
      }"
      class="text-sm text-zinc-700 dark:text-zinc-300"
    >
      <div
        v-if="!field.wrapping"
        class="nova-index-field-truncate"
        :title="fieldTooltip(field)"
      >
        <component
          :is="'index-' + field.component"
          :class="`text-${field.textAlign}`"
          :field="field"
          :resource="resource"
          :resource-name="resourceName"
          :via-resource="viaResource"
          :via-resource-id="viaResourceId"
        />
      </div>
      <component
        v-else
        :is="'index-' + field.component"
        :class="`text-${field.textAlign}`"
        :field="field"
        :resource="resource"
        :resource-name="resourceName"
        :via-resource="viaResource"
        :via-resource-id="viaResourceId"
      />
    </td>

    <td
      :class="{
        'py-4': !shouldShowTight,
        'cursor-pointer': resource.authorizedToView,
      }"
      class="td-fit ps-5 pe-5 py-4 text-end align-middle"
    >
      <div class="flex items-center justify-end gap-1 text-zinc-400">
        <InlineActionDropdown
          v-if="shouldShowActionDropdown"
          :actions="availableActions"
          :endpoint="actionsEndpoint"
          :resource="resource"
          :resource-name="resourceName"
          :via-many-to-many="viaManyToMany"
          :via-resource="viaResource"
          :via-resource-id="viaResourceId"
          :via-relationship="viaRelationship"
          @actionExecuted="$emit('actionExecuted')"
          @show-preview="navigateToPreviewView"
        />

        <!-- View Resource Link -->
        <Button
          v-if="authorizedToViewAnyResources"
          :as="resource.authorizedToView ? 'Link' : 'Button'"
          v-tooltip.click="__('View')"
          :aria-label="__('View')"
          :dusk="`${resource['id'].value}-view-button`"
          @click.stop="navigateToDetailView"
          icon="eye"
          variant="action"
          :class="
            resource.authorizedToView
              ? 'hover:text-primary-500 dark:hover:text-primary-500'
              : null
          "
          :disabled="!resource.authorizedToView"
        />

        <!-- Edit Button -->
        <Button
          v-if="authorizedToUpdateAnyResources"
          :as="resource.authorizedToUpdate ? 'Link' : 'Button'"
          v-tooltip.click="viaManyToMany ? __('Edit Attached') : __('Edit')"
          :aria-label="viaManyToMany ? __('Edit Attached') : __('Edit')"
          :dusk="
            viaManyToMany
              ? `${resource['id'].value}-edit-attached-button`
              : `${resource['id'].value}-edit-button`
          "
          @click.stop="navigateToEditView"
          icon="pencil-square"
          variant="action"
          :class="
            resource.authorizedToUpdate
              ? 'text-brand-500 hover:text-brand-600 dark:text-brand-400 dark:hover:text-brand-300'
              : null
          "
          :disabled="!resource.authorizedToUpdate"
        />

        <!-- Delete Resource Link -->
        <Button
          v-if="
            (authorizedToDeleteAnyResources) &&
            (!resource.softDeleted || viaManyToMany)
          "
          @click.stop="openDeleteModal"
          v-tooltip.click="__(viaManyToMany ? 'Detach' : 'Delete')"
          :aria-label="__(viaManyToMany ? 'Detach' : 'Delete')"
          :dusk="`${resource.id.value}-delete-button`"
          icon="trash"
          variant="action"
          :class="{
            'text-yellow-500': resource.reasonForConditionalDeletion != ''
          }"
          :disabled="!resource.authorizedToDelete && resource.reasonForConditionalDeletion == ''"
        />

        <!-- Restore Resource Link -->
        <Button
          v-if="
            authorizedToRestoreAnyResources &&
            resource.softDeleted &&
            !viaManyToMany
          "
          v-tooltip.click="__('Restore')"
          :aria-label="__('Restore')"
          :disabled="!resource.authorizedToRestore"
          :dusk="`${resource.id.value}-restore-button`"
          type="button"
          @click.stop="openRestoreModal"
          icon="arrow-path"
          variant="action"
        />

        <DeleteResourceModal
          :mode="viaManyToMany ? 'detach' : 'delete'"
          :show="deleteModalOpen"
          :reasonForConditionalDeletion="resource.reasonForConditionalDeletion"
          @close="closeDeleteModal"
          @confirm="confirmDelete"
        />

        <RestoreResourceModal
          :show="restoreModalOpen"
          @close="closeRestoreModal"
          @confirm="confirmRestore"
        >
          <ModalHeader v-text="__('Restore Resource')" />
          <ModalContent>
            <p class="leading-normal">
              {{ __('Are you sure you want to restore this resource?') }}
            </p>
          </ModalContent>
        </RestoreResourceModal>
      </div>
    </td>
  </tr>

  <PreviewResourceModal
    v-if="previewModalOpen"
    :resource-id="resource.id.value"
    :resource-name="resourceName"
    :show="previewModalOpen"
    @close="closePreviewModal"
    @confirm="closePreviewModal"
  />
</template>

<script>
import filter from 'lodash/filter'
import { Inertia } from '@inertiajs/inertia'
import { mapGetters } from 'vuex'
import { Button, Checkbox, Icon } from 'laravel-nova-ui'

export default {
  components: {
    Button,
    Checkbox,
    Icon,
  },

  emits: ['actionExecuted'],

  inject: [
    'authorizedToViewAnyResources',
    'authorizedToUpdateAnyResources',
    'authorizedToDeleteAnyResources',
    'authorizedToRestoreAnyResources',
  ],

  props: [
    'actionsAreAvailable',
    'actionsEndpoint',
    'checked',
    'clickAction',
    'deleteResource',
    'queryString',
    'relationshipType',
    'resource',
    'resourceName',
    'resourcesSelected',
    'restoreResource',
    'selectedResources',
    'shouldShowCheckboxes',
    'shouldShowColumnBorders',
    'tableStyle',
    'testId',
    'updateSelectionStatus',
    'viaManyToMany',
    'viaRelationship',
    'viaResource',
    'viaResourceId',
  ],

  data: () => ({
    commandPressed: false,
    deleteModalOpen: false,
    restoreModalOpen: false,
    previewModalOpen: false,
  }),

  beforeMount() {
    this.isSelected = this.selectedResources.indexOf(this.resource) > -1
  },

  mounted() {
    window.addEventListener('keydown', this.handleKeydown)
    window.addEventListener('keyup', this.handleKeyup)
  },

  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeydown)
    window.removeEventListener('keyup', this.handleKeyup)
  },

  methods: {
    /**
     * Select the resource in the parent component
     */
    toggleSelection() {
      this.updateSelectionStatus(this.resource)
    },

    handleKeydown(e) {
      if (e.key === 'Meta' || e.key === 'Control') {
        this.commandPressed = true
      }
    },

    handleKeyup(e) {
      if (e.key === 'Meta' || e.key === 'Control') {
        this.commandPressed = false
      }
    },

    handleClick(e) {
      if (this.clickAction === 'edit') {
        return this.navigateToEditView(e)
      } else if (this.clickAction === 'select') {
        return this.toggleSelection()
      } else if (this.clickAction === 'ignore') {
        return
      } else if (this.clickAction === 'detail') {
        return this.navigateToDetailView(e)
      } else if (this.clickAction === 'preview') {
        return this.navigateToPreviewView(e)
      } else {
        return this.navigateToDetailView(e)
      }
    },

    navigateToDetailView(e) {
      if (!this.resource.authorizedToView) {
        return
      }
      this.commandPressed
        ? window.open(this.viewURL, '_blank')
        : Inertia.visit(this.viewURL)
    },

    navigateToEditView(e) {
      if (!this.resource.authorizedToUpdate) {
        return
      }
      this.commandPressed
        ? window.open(this.updateURL, '_blank')
        : Inertia.visit(this.updateURL)
    },

    navigateToPreviewView(e) {
      if (!this.resource.authorizedToView) {
        return
      }
      this.openPreviewModal()
    },

    openPreviewModal() {
      this.previewModalOpen = true
    },

    closePreviewModal() {
      this.previewModalOpen = false
    },

    openDeleteModal() {
      this.deleteModalOpen = true
    },

    confirmDelete() {
      this.deleteResource(this.resource)
      this.closeDeleteModal()
    },

    closeDeleteModal() {
      this.deleteModalOpen = false
    },

    openRestoreModal() {
      this.restoreModalOpen = true
    },

    confirmRestore() {
      this.restoreResource(this.resource)
      this.closeRestoreModal()
    },

    closeRestoreModal() {
      this.restoreModalOpen = false
    },

    fieldTooltip(field) {
      if (field.wrapping) {
        return undefined
      }

      const label = field.displayedAs ?? field.value

      if (typeof label === 'string' && label.length > 0) {
        return label
      }

      return undefined
    },
  },

  computed: {
    ...mapGetters(['currentUser']),

    updateURL() {
      if (this.viaManyToMany) {
        return this.$url(
          `/resources/${this.viaResource}/${this.viaResourceId}/edit-attached/${this.resourceName}/${this.resource.id.value}`,
          {
            viaRelationship: this.viaRelationship,
            viaPivotId: this.resource.id.pivotValue,
          }
        )
      }

      return this.$url(
        `/resources/${this.resourceName}/${this.resource.id.value}/edit`,
        {
          viaResource: this.viaResource,
          viaResourceId: this.viaResourceId,
          viaRelationship: this.viaRelationship,
        }
      )
    },

    viewURL() {
      return this.$url(
        `/resources/${this.resourceName}/${this.resource.id.value}`
      )
    },

    availableActions() {
      return filter(this.resource.actions, a => a.showOnTableRow)
    },

    shouldShowTight() {
      return this.tableStyle === 'tight'
    },

    clickableRow() {
      if (this.clickAction === 'edit') {
        return this.resource.authorizedToUpdate
      } else if (this.clickAction === 'select') {
        return this.shouldShowCheckboxes
      } else if (this.clickAction === 'ignore') {
        return false
      } else if (this.clickAction === 'detail') {
        return this.resource.authorizedToView
      } else if (this.clickAction === 'preview') {
        return this.resource.authorizedToView
      } else {
        return this.resource.authorizedToView
      }
    },

    shouldShowActionDropdown() {
      return this.availableActions.length > 0 || this.userHasAnyOptions
    },

    shouldShowPreviewLink() {
      return this.resource.authorizedToView && this.resource.previewHasFields
    },

    userHasAnyOptions() {
      return (
        this.resource.authorizedToReplicate ||
        this.shouldShowPreviewLink ||
        this.canBeImpersonated
      )
    },

    canBeImpersonated() {
      return (
        this.currentUser.canImpersonate && this.resource.authorizedToImpersonate
      )
    },
  },
}
</script>

<style scoped>
.nova-index-field-truncate {
  max-width: 12rem;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.nova-index-field-truncate :deep(a) {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
}
</style>
